import { View } from "@react-pdf/renderer";
import { useContext } from "react";
import { RedSlash } from "./Components";
import { horizontalPadding, tableBorder, verticalPadding } from "./const";
import { CVContentContext, LanguageContext } from "./CVPDF";
import { ContentRow, HeaderRow } from "./TableComponents";
import { SmallTextPDF } from "./Texts";
import { cvTranslations } from "./translations";

export default function Tech() {
  const cvContent = useContext<any>(CVContentContext) || {};
  const language = useContext(LanguageContext);
  const t = cvTranslations[language];

  // react-pdf's `subPageNumber` increments across the whole document <Page>,
  // not within a specific section. Tech is forced onto its own sub-page via
  // `break`, but its first sub-page number is whatever the surrounding content
  // (Header + FirstPageContent) leaves us at. To know "first Tech page vs
  // continuation", capture the first subPageNumber observed here — the layout
  // pass and the render pass both walk sub-pages in order, so the very first
  // value is always the first appearance of Tech.
  let firstSubPageNumber: number | null = null;

  const getTranslatedLabel = (label: string) => {
    if (label === 'Industries') {
      return language === 'de' ? 'Branchen' : 'Industries';
    }
    return label;
  };
  // Per-row borders rather than a single outer border on the wrappable
  // container. The outer-border approach made the bordered View draw its
  // vertical lines all the way to the bottom of every page it occupied —
  // including the empty space below the last row on the first Tech page.
  // With each row carrying its own left/right (+ top on header, bottom on
  // every row), the table ends visually exactly at its last row.
  const sideBorders = {
    borderLeft: tableBorder,
    borderRight: tableBorder,
  };

  return (
    // `break` on the OUTER wrapper, `wrap={true}` on the inner View. Setting
    // both on the SAME View triggers a Yoga crash ("RangeError: Invalid array
    // length") in @react-pdf/renderer v4 when the tech content overflows a
    // single page. Splitting them onto two sibling Views avoids the conflict.
    <View break>
      <View
        style={{
          marginLeft: horizontalPadding,
          marginRight: horizontalPadding,
        }}
        wrap={true}
      >
        {/*
          Fixed top spacer — repeats on every Tech sub-page (including the
          first), giving consistent breathing room above the header on both
          the first page and continuation pages. (The previous `marginTop`
          on the wrappable container only applied to the first sub-page.)
        */}
        <View fixed style={{ height: verticalPadding }} />

        <HeaderRow
          fixed
          renderLabel={({ subPageNumber }) => {
            // react-pdf runs several layout-probe passes before pagination is
            // finalised, during which `subPageNumber` is `undefined`. Only the
            // real per-sub-page render passes pass a numeric value — and they
            // arrive in order. Capture the first numeric value as our
            // baseline; anything greater is a continuation page.
            if (typeof subPageNumber !== "number") {
              return t.industryTechnicalExpertise;
            }
            if (firstSubPageNumber === null) {
              firstSubPageNumber = subPageNumber;
            }
            return subPageNumber > firstSubPageNumber
              ? t.industryTechnicalExpertise + t.continuedSuffix
              : t.industryTechnicalExpertise;
          }}
          style={{
            borderTop: tableBorder,
            ...sideBorders,
          }}
        />
        {Array.isArray(cvContent["tech"]) &&
          cvContent["tech"]
            .sort((tech) => {
              const key = Object.keys(tech)[0];
              return (key === "Industries" || key === "Branchen") ? -1 : 1;
            })
            .map((tech) => {
              const firstKey = Object.keys(tech)[0];
              const values = tech[firstKey] || [];
              return (
                // Every row keeps its bottom border (no special-casing the
                // last row): with no outer container border, the last row's
                // bottom border IS the bottom of the table.
                <ContentRow
                  key={firstKey}
                  label={getTranslatedLabel(firstKey)}
                  content={
                    <SmallTextPDF>
                      {values.map((value, index) => (
                        <SmallTextPDF key={index}>
                          {value}
                          {index < values.length - 1 && <RedSlash />}
                        </SmallTextPDF>
                      ))}
                    </SmallTextPDF>
                  }
                  style={sideBorders}
                />
              );
            })}
      </View>
    </View>
  );
}
